// Promo / coupon codes.
//
// HARD business rule (client's): a promo discounts the SUBTOTAL only — it can
// NEVER reduce or waive the delivery fee, and it can never push the payable
// amount below zero. Delivery fee and the minimum-order check are always based
// on the pre-discount subtotal, so a coupon can't sneak an order under the
// delivery minimum or make delivery free.
//
// This list is the editable source of truth for the demo / launch. When the
// backend is live the same codes live server-side and the Cloud Function
// re-validates before charging — the client value here is only a preview.

import { round3 } from "../lib/pricing";

export interface Promo {
  code: string; // stored/compared upper-cased, no spaces
  type: "percent" | "fixed";
  value: number; // percent (0–100) OR fixed KWD off the subtotal
  /** Minimum subtotal (KWD) required for the code to apply. */
  minSubtotal?: number;
  /** Cap on the discount (KWD) — mainly for percentage codes. */
  maxDiscount?: number;
  active: boolean;
  /** Optional expiry as an ISO date (YYYY-MM-DD), inclusive of that day. */
  expiresAt?: string;
  labelEn: string;
  labelAr: string;
}

// --- Edit these to run campaigns ---------------------------------------------
export const PROMOS: Promo[] = [
  {
    code: "WELCOME10",
    type: "percent",
    value: 10,
    minSubtotal: 3,
    maxDiscount: 2,
    active: true,
    labelEn: "10% off your order (up to 2.000 KWD)",
    labelAr: "خصم ١٠٪ على طلبك (حتى ٢٫٠٠٠ د.ك)",
  },
  {
    code: "SWEET15",
    type: "percent",
    value: 15,
    minSubtotal: 8,
    maxDiscount: 3,
    active: true,
    labelEn: "15% off orders over 8.000 KWD (up to 3.000 KWD)",
    labelAr: "خصم ١٥٪ للطلبات فوق ٨٫٠٠٠ د.ك (حتى ٣٫٠٠٠ د.ك)",
  },
  {
    code: "KD1OFF",
    type: "fixed",
    value: 1,
    minSubtotal: 5,
    active: true,
    labelEn: "1.000 KWD off orders over 5.000 KWD",
    labelAr: "خصم ١٫٠٠٠ د.ك للطلبات فوق ٥٫٠٠٠ د.ك",
  },
];
// -----------------------------------------------------------------------------

export type PromoResult =
  | { ok: true; discount: number; promo: Promo }
  | { ok: false; reason: "invalid" | "expired" | "belowMin"; minSubtotal?: number };

export function normalizeCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, "");
}

function isExpired(promo: Promo): boolean {
  if (!promo.expiresAt) return false;
  // Compare on the Kuwait calendar day so a code is valid through its last day.
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kuwait",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  return today > promo.expiresAt;
}

/** Discount is applied to the subtotal only, capped so it never exceeds it. */
export function discountFor(promo: Promo, subtotal: number): number {
  const raw =
    promo.type === "percent" ? (subtotal * promo.value) / 100 : promo.value;
  const capped = promo.maxDiscount != null ? Math.min(raw, promo.maxDiscount) : raw;
  return round3(Math.max(0, Math.min(capped, subtotal)));
}

export function validatePromo(rawCode: string, subtotal: number): PromoResult {
  const code = normalizeCode(rawCode);
  const promo = PROMOS.find((p) => p.code === code && p.active);
  if (!promo) return { ok: false, reason: "invalid" };
  if (isExpired(promo)) return { ok: false, reason: "expired" };
  if (promo.minSubtotal != null && subtotal < promo.minSubtotal) {
    return { ok: false, reason: "belowMin", minSubtotal: promo.minSubtotal };
  }
  return { ok: true, discount: discountFor(promo, subtotal), promo };
}
